import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
  split,
  Operation,
  Reference,
} from '@apollo/client';
import { createFragmentRegistry } from '@apollo/client/cache';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  DEVICE_LIST_DGA_INFO_FRAGMENT,
  DEVICE_LIST_INFO_FRAGMENT,
  DEVICE_LIST_METRICS_FRAGMENT,
  DEVICE_LIST_MINICHARTS_FRAGMENT,
  DEVICE_LIST_PICTURE_FRAGMENT,
} from '@features/devices2/graphql/queries/getDevices';
import { GATE_MOVEMENT_FRAGMENT } from '@features/gateCommon/graphql/fragments/gateMovementFragment';
import { GATE_MOVEMENT_HISTORY_FRAGMENT } from '@features/gateCommon/graphql/fragments/gateMovementHistoryFragment';
import {
  METRICS_VIEW_BUTTON_WIDGET_FIELDS_FRAGMENT,
  METRICS_VIEW_CHART_WIDGET_FIELDS_FRAGMENT,
  METRICS_VIEW_DATA_WIDGET_FIELDS_FRAGMENT,
  METRICS_VIEW_GENERIC_WIDGET_FIELDS_FRAGMENT,
  METRICS_VIEW_TABLE_WIDGET_FIELDS_FRAGMENT,
  METRICS_VIEW_TEXT_WIDGET_FIELDS_FRAGMENT,
} from '@features/metricsViews/graphql/queries/widgetsFragment';
import { createClient } from 'graphql-ws';
import { forceLogout } from 'src/utils/forceLogout';

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GRAPHQL_WS_URL,
    // Función, no objeto: graphql-ws la evalúa en cada (re)conexión. Como objeto quedaba
    // congelado el token del module load, y en /login eso es ''. Con lazy (default) el
    // socket abre con la primera subscription — que es justo al loguearse, porque
    // GlobalComponents monta LoggedInGlobalComponents detrás de isAuthenticated — así que
    // el WS arrancaba sin auth, el backend rechazaba con NOT_AUTHENTICATED y el errorLink
    // deslogueaba. Recargar ya logueado lo tapaba: ahí el module load sí ve el token.
    connectionParams: () => {
      const token = window.localStorage.getItem('accessToken');
      return { authorization: token ? `Bearer ${token}` : '' };
    },
    retryAttempts: Infinity,
    shouldRetry: () => true,
    keepAlive: 10000,
  }),
);

const authLink = setContext((_, { headers }) => {
  const token = window.localStorage.getItem('accessToken');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const batchKey = (operation: Operation) => {
  // Agrupar por nombre de operación
  const operationName = operation.operationName;
  if (operationName === 'GetBillingAdministrationOrganizations') {
    return 'batch-GetBillingAdministrationOrganizations';
  } else if (operationName === 'GetBillingAdministration') {
    return 'batch-GetBillingAdministration';
  } else if (operationName === 'GetBillingAdministrationProjects') {
    return 'batch-GetBillingAdministrationProjects';
  }
  return operation.operationName;
};

const batchHttpLink = new BatchHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL,
  // batchMax: 5, // No more than 5 operations per batch
  batchInterval: 20, // Wait no more than 20ms after first batched operation
  batchKey,
});

const httpLinkSplit = split(
  () => {
    const token = window.localStorage.getItem('accessToken');
    return !token;
  },
  authLink.concat(httpLink),
  authLink.concat(batchHttpLink),
);

const link: ApolloLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLinkSplit,
);

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    let authError = false;
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      // Detectar por código de extensión (fuente de verdad del backend) con fallback
      // al string legacy para tokens emitidos antes del deploy del backend nuevo.
      //
      // Solo UNAUTHENTICATED significa "esta sesión no vale": lo lanzan únicamente los dos
      // gates de context (backend/src/graphql/index.ts, enforceActiveUser y el chequeo de
      // !user). NOT_AUTHENTICATED lo tira cualquier resolver vía @Auth.loggedIn cuando le
      // falta user, lo que no es evidencia de sesión muerta — deslogear por eso convierte
      // un error puntual de una operación en la expulsión de toda la sesión.
      const code = extensions?.code;
      if (code === 'UNAUTHENTICATED' || message.match(/you must be logged in/)) {
        authError = true;
      }
    });
    if (authError) {
      // Limpia las 5 keys de sesión y recarga en /login (reset completo del client).
      forceLogout();
    }
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const removeTypenameLink = removeTypenameFromVariables();

export const client = new ApolloClient({
  link: from([removeTypenameLink, errorLink, link]),
  cache: new InMemoryCache({
    fragments: createFragmentRegistry(
      DEVICE_LIST_INFO_FRAGMENT,
      DEVICE_LIST_METRICS_FRAGMENT,
      DEVICE_LIST_MINICHARTS_FRAGMENT,
      DEVICE_LIST_PICTURE_FRAGMENT,
      DEVICE_LIST_DGA_INFO_FRAGMENT,
      METRICS_VIEW_DATA_WIDGET_FIELDS_FRAGMENT,
      METRICS_VIEW_CHART_WIDGET_FIELDS_FRAGMENT,
      METRICS_VIEW_TABLE_WIDGET_FIELDS_FRAGMENT,
      METRICS_VIEW_BUTTON_WIDGET_FIELDS_FRAGMENT,
      METRICS_VIEW_TEXT_WIDGET_FIELDS_FRAGMENT,
      METRICS_VIEW_GENERIC_WIDGET_FIELDS_FRAGMENT,
      GATE_MOVEMENT_FRAGMENT,
      GATE_MOVEMENT_HISTORY_FRAGMENT,
    ),
    typePolicies: {
      Query: {
        fields: {
          // https://www.apollographql.com/docs/react/caching/advanced-topics/#cache-redirects
          device: {
            read(_, { args, toReference }) {
              if (!args?.input) {
                return undefined;
              }
              return toReference({
                __typename: 'Device',
                id: args.input.id,
              });
            },
          },
          reportTemplate: {
            read(_, { args, toReference }) {
              if (!args?.id) {
                return undefined;
              }
              return toReference({
                __typename: 'ReportTemplate',
                id: args.id,
              });
            },
          },
          gate: {
            read(_, { args, toReference }) {
              if (!args?.input) {
                return undefined;
              }
              return toReference({
                __typename: 'Gate2',
                id: args.input.id,
              });
            },
          },
          user: {
            read(_, { args, toReference }) {
              if (!args) {
                try {
                  const user = JSON.parse(localStorage.getItem('user')!);
                  return toReference({ __typename: 'User2', id: user.id });
                } catch {
                  return undefined;
                }
              } else
                return toReference({
                  __typename: 'User2',
                  id: args.id,
                });
            },
          },
          deviceObservation(_, { args, toReference }) {
            return toReference({
              __typename: 'DeviceObservation',
              id: args!.id,
            });
          },
          announcement: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'Announcement',
                id: args!.id,
              });
            },
          },
          auditLogs: {
            keyArgs: ['input'],
            merge(existing, incoming, { readField }) {
              const items: Record<string, unknown> = existing ? { ...existing.items } : {};
              incoming.items.forEach((item: Reference) => {
                items[readField('id', item) as string] = item;
              });
              return {
                items,
                pageInfo: incoming.pageInfo,
                access: incoming.access,
              };
            },

            read(existing) {
              if (existing) {
                return {
                  items: Object.values(existing.items),
                  pageInfo: existing.pageInfo,
                };
              }
            },
          },
          integration: {
            read(_, { args, toReference }) {
              if (args?.input?.id == null) return undefined;
              return toReference({
                __typename: 'Integration',
                id: args.input.id,
              });
            },
          },
          monitoredTarget: {
            read(_, { args, toReference }) {
              if (args?.input?.id == null) return undefined;
              return toReference({
                __typename: 'MonitoredTarget',
                id: args.input.id,
              });
            },
          },
        },
      },
      AlertsNotifications: {
        merge: true,
        fields: {
          history: {
            keyArgs: ['input'],
          },
        },
      },
      DeviceObservationsPagination: {
        keyFields: ['id', 'pageInfo'],
      },
      DeviceDataConfiguration: {
        keyFields: ['deviceId'],
      },
      DgaDeviceNotification: {
        keyFields: ['deviceId'],
      },
      MetricsView: {
        keyFields: ['masterId'],
      },
      // El id de un widget es único solo dentro de su tablero: se guardan embebidos en la vista.
      MetricsViewDataWidget: { keyFields: false },
      MetricsViewChartWidget: { keyFields: false },
      MetricsViewTableWidget: { keyFields: false },
      MetricsViewButtonWidget: { keyFields: false },
      MetricsViewTextWidget: { keyFields: false },
      MetricsViewImageWidget: { keyFields: false },
      MetricsViewGenericWidget: { keyFields: false },
      canalUser: {
        keyFields: ['id'],
      },
      notification: {
        merge: true,
      },
      UserPreferencesMetricsField: {
        merge: false,
      },
      Metrics: {
        merge: true,
      },
      // Sin id: se guardan embebidos bajo ROOT_QUERY.authorization. Varias queries piden distintos
      // subconjuntos de campos; `merge: true` los combina en vez de pisarlos.
      Authorization: {
        merge: true,
      },
      AuthorizationGeneral: {
        merge: true,
      },
      MetricTimeseries: {
        merge: true,
      },
    },
  }),
  ssrMode: false,
  connectToDevTools: true,
});
